// Push registration. Two paths depending on runtime:
//
//   - Native (iOS / Android via Capacitor wrapper), uses the
//     @capacitor/push-notifications plugin, gets an APNs / FCM device
//     token, POSTs to /api/me/push with platform="ios" | "android"
//   - Web (browser PWA / Safari add-to-home), uses the Web Push API,
//     creates a subscription with VAPID, POSTs the subscription JSON
//     to /api/me/push with platform="web"
//
// PushSettings calls these, the branching lives here so the UI
// doesn't have to know which runtime it's in.
import { API_BASE } from "./api";
import {
  enqueuePushOpen,
  readPushOpenQueue,
  removePushOpenFromQueue,
} from "./push-open-queue";

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY as
  | string
  | undefined;

// Lazy-imported Capacitor stays out of the web bundle for browser
// users. Returns undefined when not running inside Capacitor.
async function getCapacitor() {
  try {
    const { Capacitor } = await import("@capacitor/core");
    if (!Capacitor.isNativePlatform()) return null;
    return Capacitor;
  } catch {
    return null;
  }
}

export function pushSupported(): boolean {
  // Aligned with the Next.js app: push is NATIVE-ONLY (Capacitor → APNs/FCM).
  // We deliberately do NOT offer browser/web push (VAPID), it's unreliable,
  // especially on iOS Safari (needs an installed PWA, and even then flaky), so
  // the old app only ever enabled push inside the native app. In a plain
  // browser this returns false, so /account shows the "use the app" card
  // instead of attempting a subscription that can't be delivered.
  if (typeof window === "undefined") return false;
  const cap = (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
  return !!(cap && cap.isNativePlatform && cap.isNativePlatform());
}

export function pushPermission(): NotificationPermission {
  return typeof Notification === "undefined" ? "default" : Notification.permission;
}

export async function ensureServiceWorker(): Promise<ServiceWorkerRegistration> {
  return navigator.serviceWorker.register("/sw.js", { scope: "/" });
}

export async function subscribeToPush(venueId: string): Promise<void> {
  const cap = await getCapacitor();
  if (cap) {
    await subscribeNative(venueId, cap);
    return;
  }
  await subscribeWeb(venueId);
}

export async function unsubscribeFromPush(): Promise<void> {
  const cap = await getCapacitor();
  if (cap) {
    await unsubscribeNative();
    return;
  }
  await unsubscribeWeb();
}

// "Am I already subscribed?", branches the same way subscribe does so
// callers don't have to know which runtime they're in. In a Capacitor
// WebView, navigator.serviceWorker doesn't exist, so we treat
// "registered server-side as a native token" as the source of truth
// and just return false here (the native register flow is idempotent;
// the server upserts on the device token).
export async function isSubscribedToPush(): Promise<boolean> {
  const cap = await getCapacitor();
  if (cap) {
    // Native: the source of truth is the server-side token, same as the
    // Next.js app (which renders state from tokens.length). The Capacitor
    // WebView has no serviceWorker/PushManager to inspect, and the old code
    // hard-returned false here, so the toggle never reflected that the
    // device was registered and reverted to "off" on every reload.
    try {
      const res = await fetch(`${API_BASE}/api/me/push`, { credentials: "include" });
      if (!res.ok) return false;
      const { tokens } = (await res.json()) as { tokens: { platform: string }[] };
      const platform = cap.getPlatform(); // "ios" | "android"
      return tokens.some((t) => t.platform === platform);
    } catch {
      return false;
    }
  }
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return false;
  try {
    const reg = await navigator.serviceWorker.getRegistration("/");
    if (!reg) return false;
    const sub = await reg.pushManager.getSubscription();
    return !!sub;
  } catch {
    return false;
  }
}

// ─── Native (Capacitor) ─────────────────────────────────────────────────

/**
 * Fire the OS permission dialog WITHOUT registering a token. Useful
 * for the signed-out welcome prompt: the user grants permission at
 * high-intent moment, we save that fact to localStorage, and register
 * the token later on signup (when a `userId` exists to hang it off).
 *
 * Returns the raw plugin result: 'granted' | 'denied' | 'prompt' |
 * 'prompt-with-rationale'. Callers typically fold the two 'prompt*'
 * variants into 'prompt'.
 */
export async function requestNativePushPermission(): Promise<
  "granted" | "denied" | "prompt" | "prompt-with-rationale"
> {
  const cap = await getCapacitor();
  if (!cap) return "denied";
  const { PushNotifications } = await import("@capacitor/push-notifications");
  const permission = await PushNotifications.requestPermissions();
  return permission.receive;
}

/**
 * Check the current OS-level push permission without prompting the user.
 * Returns 'granted' | 'denied' | 'prompt' (or 'prompt-with-rationale').
 * Used at post-signup time to decide whether we can register the token
 * silently (permission already granted) or have to defer to the
 * signed-in modal.
 */
export async function checkNativePushPermission(): Promise<
  "granted" | "denied" | "prompt" | "prompt-with-rationale"
> {
  const cap = await getCapacitor();
  if (!cap) return "denied";
  const { PushNotifications } = await import("@capacitor/push-notifications");
  const permission = await PushNotifications.checkPermissions();
  return permission.receive;
}

/**
 * Does the current runtime support opening the OS notification-settings
 * screen? Currently true on iOS only. Android needs a native intent
 * (Settings.ACTION_APP_NOTIFICATION_SETTINGS) which isn't exposed
 * through @capacitor/app; ship follow-up plugin work if/when a venue
 * launches on Android.
 *
 * Web browsers have no equivalent (browser notification permission
 * lives in Chrome/Safari settings, no deep link).
 */
export function isOsNotificationSettingsSupported(): boolean {
  if (typeof window === "undefined") return false;
  const cap = (window as unknown as {
    Capacitor?: { isNativePlatform?: () => boolean; getPlatform?: () => string };
  }).Capacitor;
  if (!cap?.isNativePlatform?.()) return false;
  return cap.getPlatform?.() === "ios";
}

/**
 * Open the OS notification-settings screen for this app, so the user
 * can toggle banners/badges/sounds/Focus allowlist without leaving
 * our UI's flow.
 *
 * iOS: the `app-settings:` URL scheme deep-links to Settings > [this
 * app]. Capacitor 8's `@capacitor/app` removed the `openUrl` helper,
 * so we set `window.location.href` directly; the Capacitor WebView
 * delegates unknown URL schemes to the OS, which handles the deep
 * link. Same pattern the wallet-pass code uses for `shoebox:` URLs.
 *
 * Android + web: no-op today (see isOsNotificationSettingsSupported;
 * Android needs a native intent that this plugin doesn't expose).
 *
 * Never throws upstream; caller catches + no-ops just in case.
 */
export async function openOsNotificationSettings(): Promise<void> {
  if (!isOsNotificationSettingsSupported()) return;
  if (typeof window === "undefined") return;
  window.location.href = "app-settings:";
}

async function subscribeNative(
  venueId: string,
  cap: { getPlatform(): string },
): Promise<void> {
  const { PushNotifications } = await import("@capacitor/push-notifications");
  const permission = await PushNotifications.requestPermissions();
  if (permission.receive !== "granted") {
    throw new Error("Push permission denied");
  }
  // Register and wait for the OS to hand us a device token. The
  // listener fires asynchronously after register() resolves.
  await new Promise<void>((resolve, reject) => {
    let settled = false;
    PushNotifications.addListener("registration", async (token) => {
      if (settled) return;
      settled = true;
      try {
        const { App } = await import("@capacitor/app");
        const info = await App.getInfo();
        const res = await fetch(`${API_BASE}/api/me/push`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            venueId,
            // Native side stores the raw device token in `subscription`
            // (the field is generic, the server's PushToken row uses
            // it for whatever the platform considers its token).
            subscription: token.value,
            platform: cap.getPlatform(), // "ios" | "android"
            bundleId: info.id,
          }),
        });
        if (!res.ok) throw new Error(`token register failed: ${res.status}`);
        resolve();
      } catch (e) {
        reject(e);
      }
    });
    PushNotifications.addListener("registrationError", (e) => {
      if (settled) return;
      settled = true;
      reject(new Error(typeof e === "string" ? e : JSON.stringify(e)));
    });
    PushNotifications.register().catch((e) => {
      if (!settled) {
        settled = true;
        reject(e);
      }
    });
  });

  // Set up the deep-link tap handler once. Multiple addListener calls
  // accumulate but the dedup happens server-side at delivery so it
  // doesn't matter much.
  PushNotifications.addListener("pushNotificationActionPerformed", (n) => {
    const data = n.notification.data as
      | { url?: string; campaignId?: string }
      | undefined;
    if (data?.campaignId) {
      void reportPushOpen(data.campaignId);
    }
    if (data?.url) window.location.href = data.url;
  });
}

/**
 * Best-effort beacon to POST /api/me/push-open. Fires immediately
 * against the server; on 401 (iOS cold-launch: listener fires before
 * the session cookie is validated) or any other failure, enqueues
 * for replay via `flushPushOpenQueue()` when auth resolves.
 *
 * Peer-review 2026-09-29 HIGH: without the localStorage queue, a
 * lock-screen tap on a cold-launched app dropped the open silently
 * and made the dashboard read "0% open rate" even when every user
 * tapped. That would have made the admin panel a lie for the Love
 * Inn launch this weekend.
 */
export async function reportPushOpen(
  campaignId: string,
  kind = "opened",
): Promise<void> {
  try {
    const res = await fetch(`${API_BASE}/api/me/push-open`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ campaignId, kind }),
      keepalive: true,
    });
    if (res.status === 401 || !res.ok) {
      enqueuePushOpen({ campaignId, kind });
    }
  } catch {
    enqueuePushOpen({ campaignId, kind });
  }
}

/**
 * Flush every queued open beacon. Called on successful auth
 * resolution (see `<PushOpenQueueFlusher>` mounted at RootComponent).
 * Removes queue entries only on definitive server acknowledgement
 * (200); leaves them in place on 401 / 5xx so the next flush retries.
 * A 400 with "campaign_not_found" also removes the entry (the
 * campaign was deleted; no point retrying).
 */
export async function flushPushOpenQueue(): Promise<void> {
  const queued = readPushOpenQueue();
  for (const q of queued) {
    try {
      const res = await fetch(`${API_BASE}/api/me/push-open`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ campaignId: q.campaignId, kind: q.kind }),
      });
      if (res.ok) {
        removePushOpenFromQueue({ campaignId: q.campaignId, kind: q.kind });
      } else if (res.status === 400) {
        // Unrecoverable (bad campaignId etc). Drop so we don't retry
        // forever. Server's other 4xx paths would also be
        // unrecoverable (400 = "campaign_not_found" from the handler).
        removePushOpenFromQueue({ campaignId: q.campaignId, kind: q.kind });
      }
      // 401 / 5xx: leave queued; next flush retries.
    } catch {
      // Network failure: leave queued; next flush retries.
    }
  }
}

async function unsubscribeNative(): Promise<void> {
  // Best-effort, Capacitor doesn't expose a "deregister with APNs"
  // call, so we just drop the server-side row and let the OS suppress
  // future deliveries when the user denies permission in settings.
  await fetch(`${API_BASE}/api/me/push`, {
    method: "DELETE",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({}),
  });
}

// ─── Web (browser PushManager + VAPID) ──────────────────────────────────
async function subscribeWeb(venueId: string): Promise<void> {
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error("Notification permission denied");
  }
  const reg = await ensureServiceWorker();
  let subscription = await reg.pushManager.getSubscription();
  if (!subscription) {
    if (!VAPID_PUBLIC_KEY) {
      throw new Error(
        "VAPID public key not configured, set VITE_VAPID_PUBLIC_KEY at build time",
      );
    }
    subscription = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(
        VAPID_PUBLIC_KEY,
      ) as unknown as BufferSource,
    });
  }
  await fetch(`${API_BASE}/api/me/push`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      venueId,
      subscription: subscription.toJSON(),
      platform: "web",
    }),
  });
}

async function unsubscribeWeb(): Promise<void> {
  if (!("serviceWorker" in navigator)) return;
  const reg = await navigator.serviceWorker.getRegistration("/");
  if (!reg) return;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  await fetch(`${API_BASE}/api/me/push`, {
    method: "DELETE",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ subscription: sub.toJSON() }),
  });
  await sub.unsubscribe();
}

// VAPID public key arrives as a URL-safe base64 string. The Push API
// wants a Uint8Array. Standard polyfill.
function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const decoded = atob((base64 + padding).replace(/-/g, "+").replace(/_/g, "/"));
  const arr = new Uint8Array(decoded.length);
  for (let i = 0; i < decoded.length; i++) arr[i] = decoded.charCodeAt(i);
  return arr;
}
